/**
 * CevalPanel — lichess-style local-engine box for the Study analysis tab.
 *
 * Presentational only: the engine itself runs headlessly at the screen level
 * (so practice mode keeps its opponent even when this panel isn't mounted)
 * and feeds parsed PV lines down. Mirrors the lichess ceval workflow:
 *
 *   - header: big white-POV eval, engine name, "Depth n/max", on/off switch
 *   - thin progress bar tracking search depth
 *   - threat mode (crosshair button / "x" key): analyzes the position with
 *     the turn passed to the opponent and tints the box red
 *   - gear: "Multiple lines" (MultiPV 1–5) + "Best move arrow" preference
 *   - PV rows: hover previews the move as a board arrow, click plays it
 */

import React, {useState} from 'react';
import {View, Switch, TouchableOpacity} from 'react-native';
import {Chess} from 'chess.js';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import Slider from '@react-native-community/slider';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';

export interface CevalPvLine {
  /** PV in SAN, starting from the analyzed position. */
  sanMoves: string[];
  /** PV in UCI notation (for arrows / auto-play). */
  uciMoves: string[];
  /** Centipawns, white POV. Absent on mate scores. */
  cp?: number;
  /** Mate distance, white POV sign (positive = white mates). */
  mate?: number;
  depth: number;
}

interface CevalPanelProps {
  enabled: boolean;
  onToggle: (value: boolean) => void;
  /** Position on the board (used for game-over detection). */
  fen: string;
  /** Position the engine is actually searching (threat FEN in threat mode). */
  analyzedFen: string;
  pvs: CevalPvLine[];
  depth: number;
  maxDepth: number;
  threatMode: boolean;
  threatAvailable: boolean;
  onToggleThreat: () => void;
  multiPv: number;
  onMultiPvChange: (value: number) => void;
  showArrows: boolean;
  onShowArrowsChange: (value: boolean) => void;
  onHoverPv: (uci: string | null) => void;
  onPlayPvMove: (san: string) => void;
  canPlayMoves: boolean;
}

/** "+0.3" / "-1.2" / "#4" / "#-4" — lichess's white-POV eval formatting. */
export function formatWhitePovEval(pv?: CevalPvLine | null): string {
  if (!pv) {
    return '-';
  }
  if (typeof pv.mate === 'number') {
    return `#${pv.mate}`;
  }
  const pawns = (pv.cp ?? 0) / 100;
  const rounded = pawns.toFixed(1);
  return pawns > 0 ? `+${rounded}` : rounded;
}

/** Game-over label the ceval box shows in place of an eval, or null. */
function gameOverLabel(fen: string): string | null {
  try {
    const chess = new Chess(fen);
    if (chess.isCheckmate()) {
      return 'Checkmate';
    }
    if (chess.isStalemate()) {
      return 'Stalemate';
    }
    if (chess.isDraw()) {
      return 'Draw';
    }
  } catch (e) {
    return null;
  }
  return null;
}

/**
 * PV moves with lichess-style move numbers, continuing from the analyzed
 * position's fullmove counter: "3...c5 4. Nf3" etc.
 */
function pvText(sanMoves: string[], analyzedFen: string): string {
  const parts = (analyzedFen || '').split(' ');
  const blackToMove = parts[1] === 'b';
  let moveNumber = parseInt(parts[5], 10) || 1;
  const out: string[] = [];
  sanMoves.forEach((san, i) => {
    if (i === 0 && blackToMove) {
      out.push(`${moveNumber}...${san}`);
      moveNumber += 1;
      return;
    }
    const isWhiteMove = blackToMove ? i % 2 === 1 : i % 2 === 0;
    if (isWhiteMove) {
      out.push(`${moveNumber}. ${san}`);
    } else {
      out.push(san);
      moveNumber += 1;
    }
  });
  return out.join(' ');
}

const ACCENT = CCColors.Green;
const THREAT_RED = CCColors.Workbook.DeepRed;
const TEXT_DARK = CCColors.TextColor.Primary;
const TEXT_MUTED = CCColors.Workbook.Brown;
const BORDER = updateColorWithOpacity(CCColors.Workbook.Brown, 0.12);

const CevalPanel = ({
  enabled,
  onToggle,
  fen,
  analyzedFen,
  pvs,
  depth,
  maxDepth,
  threatMode,
  threatAvailable,
  onToggleThreat,
  multiPv,
  onMultiPvChange,
  showArrows,
  onShowArrowsChange,
  onHoverPv,
  onPlayPvMove,
  canPlayMoves,
}: CevalPanelProps) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const overLabel = gameOverLabel(fen);
  const bestPv = pvs[0];
  const evalText = formatWhitePovEval(bestPv);
  const progress = maxDepth > 0 ? Math.min(1, depth / maxDepth) : 0;

  return (
    <Box
      style={{
        backgroundColor: CCColors.White,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: threatMode
          ? updateColorWithOpacity(CCColors.Workbook.DeepRed, 0.35)
          : BORDER,
        overflow: 'hidden',
      }}>
      {/* Header */}
      <Box
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 12,
          paddingVertical: 8,
        }}>
        <Box style={{minWidth: 64}}>
          {overLabel ? (
            <CCText fontSize={16} fontWeight="bold" color={TEXT_DARK}>
              {overLabel}
            </CCText>
          ) : (
            <CCText
              fontSize={22}
              fontWeight="bold"
              color={
                !enabled
                  ? updateColorWithOpacity(CCColors.TextColor.Primary, 0.4)
                  : threatMode
                  ? THREAT_RED
                  : TEXT_DARK
              }>
              {enabled && bestPv ? evalText : '-'}
            </CCText>
          )}
        </Box>
        <Box style={{flex: 1, marginLeft: 10}}>
          <CCText fontSize={13} fontWeight="semibold" color={TEXT_DARK}>
            Stockfish 18
            {threatMode ? (
              <CCText fontSize={13} fontWeight="semibold" color={THREAT_RED}>
                {'  ·  Showing threat'}
              </CCText>
            ) : null}
          </CCText>
          <CCText fontSize={11} color={TEXT_MUTED}>
            {overLabel
              ? 'Game over'
              : enabled
              ? depth > 0
                ? `Depth ${depth}/${maxDepth} · in local browser`
                : 'Loading engine…'
              : 'in local browser'}
          </CCText>
        </Box>
        {enabled && !overLabel && (
          <Tip label={threatMode ? 'Hide threat (x)' : 'Show threat (x)'}>
            <TouchableOpacity
              disabled={!threatAvailable}
              onPress={onToggleThreat}
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 4,
                opacity: threatAvailable ? 1 : 0.3,
                backgroundColor: threatMode
                  ? updateColorWithOpacity(CCColors.Workbook.DeepRed, 0.12)
                  : 'transparent',
              }}>
              <MaterialCommunityIcons
                name="crosshairs"
                size={18}
                color={threatMode ? THREAT_RED : TEXT_MUTED}
              />
            </TouchableOpacity>
          </Tip>
        )}
        {enabled && (
          <TouchableOpacity
            onPress={() => setSettingsOpen(prev => !prev)}
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 4,
              backgroundColor: settingsOpen
                ? updateColorWithOpacity(CCColors.Workbook.Brown, 0.08)
                : 'transparent',
            }}>
            <Ionicons name="settings-sharp" size={16} color={TEXT_MUTED} />
          </TouchableOpacity>
        )}
        <Switch
          trackColor={{
            false: updateColorWithOpacity(CCColors.Workbook.Brown, 0.16),
            true: ACCENT,
          }}
          thumbColor={CCColors.White}
          value={enabled}
          onValueChange={onToggle}
        />
      </Box>

      {/* Depth progress bar */}
      {enabled && !overLabel && (
        <View
          style={{
            height: 3,
            backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.08),
          }}>
          <View
            style={{
              height: 3,
              width: `${progress * 100}%`,
              backgroundColor: threatMode ? THREAT_RED : ACCENT,
            }}
          />
        </View>
      )}

      {/* Settings */}
      {enabled && settingsOpen && (
        <Box
          style={{
            paddingHorizontal: 12,
            paddingVertical: 10,
            borderTopWidth: 1,
            borderTopColor: BORDER,
            backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.03),
          }}>
          <Box
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <CCText fontSize={12} color={TEXT_MUTED}>
              Multiple lines: {multiPv}
            </CCText>
            <Box style={{width: 140}}>
              <Slider
                minimumValue={1}
                maximumValue={5}
                step={1}
                value={multiPv}
                onValueChange={onMultiPvChange}
                minimumTrackTintColor={ACCENT}
              />
            </Box>
          </Box>
          <Box
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 6,
            }}>
            <CCText fontSize={12} color={TEXT_MUTED}>
              Best move arrow
            </CCText>
            <Switch
              trackColor={{false: updateColorWithOpacity(CCColors.Workbook.Brown, 0.16), true: ACCENT}}
              thumbColor={CCColors.White}
              value={showArrows}
              onValueChange={onShowArrowsChange}
            />
          </Box>
        </Box>
      )}

      {/* PV lines */}
      {enabled && !overLabel && (
        <Box style={{borderTopWidth: 1, borderTopColor: BORDER}}>
          {pvs.length === 0 ? (
            <Box style={{paddingHorizontal: 12, paddingVertical: 8}}>
              <CCText fontSize={12} color={TEXT_MUTED}>
                Calculating…
              </CCText>
            </Box>
          ) : (
            pvs.map((pv, index) =>
              pv?.sanMoves?.length ? (
                <TouchableOpacity
                  key={index}
                  activeOpacity={canPlayMoves ? 0.6 : 1}
                  onPress={() => {
                    if (canPlayMoves && pv.sanMoves[0]) {
                      onHoverPv(null);
                      onPlayPvMove(pv.sanMoves[0]);
                    }
                  }}
                  onPointerEnter={() => onHoverPv(pv.uciMoves[0] || null)}
                  onPointerLeave={() => onHoverPv(null)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderTopWidth: index === 0 ? 0 : 1,
                    borderTopColor: updateColorWithOpacity(
                      CCColors.Workbook.Brown,
                      0.06,
                    ),
                  }}>
                  <Box
                    style={{
                      minWidth: 44,
                      paddingHorizontal: 6,
                      paddingVertical: 2,
                      borderRadius: 4,
                      marginRight: 8,
                      alignItems: 'center',
                      backgroundColor: updateColorWithOpacity(
                        CCColors.Workbook.Brown,
                        0.08,
                      ),
                    }}>
                    <CCText
                      fontSize={11}
                      fontWeight="bold"
                      color={threatMode ? THREAT_RED : TEXT_DARK}>
                      {formatWhitePovEval(pv)}
                    </CCText>
                  </Box>
                  <CCText
                    fontSize={12}
                    color={TEXT_DARK}
                    numberOfLines={1}
                    style={{flex: 1}}>
                    {pvText(pv.sanMoves, analyzedFen)}
                  </CCText>
                </TouchableOpacity>
              ) : null,
            )
          )}
        </Box>
      )}
    </Box>
  );
};

/** Minimal hover tooltip (web) so the threat button documents its shortcut. */
const Tip = ({label, children}: {label: string; children: React.ReactNode}) => {
  const [show, setShow] = useState(false);
  return (
    <View
      onPointerEnter={() => setShow(true)}
      onPointerLeave={() => setShow(false)}
      style={{position: 'relative'}}>
      {children}
      {show && (
        <View
          style={{
            position: 'absolute',
            bottom: '110%',
            right: 0,
            backgroundColor: CCColors.TextColor.Dark,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 4,
            zIndex: 100,
          }}>
          <CCText
            fontSize={10}
            color={CCColors.White}
            style={{whiteSpace: 'nowrap'} as any}>
            {label}
          </CCText>
        </View>
      )}
    </View>
  );
};

export default CevalPanel;
